"use client";

import { useEffect, useMemo, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Box, CircularProgress, TextField } from "@mui/material";
import Kicker from "@/src/components/common/broadsheet/kicker";
import { fieldSx } from "@/src/components/common/broadsheet/form-kit";
import {
  FONT_TEXT,
  FONT_UI,
  hairline,
} from "@/src/components/common/broadsheet/tokens";

const collectSlugs = (nodes: any[], acc: string[] = []) => {
  nodes.forEach((node) => {
    acc.push(node.slug);
    if (node.children?.length) collectSlugs(node.children, acc);
  });
  return acc;
};

const pathToSlug = (nodes: any[], slug: string, trail: string[] = []): string[] | null => {
  for (const node of nodes) {
    if (node.slug === slug) return trail;
    if (node.children?.length) {
      const found = pathToSlug(node.children, slug, [...trail, node.slug]);
      if (found) return found;
    }
  }
  return null;
};

const flattenLeaves = (nodes: any[], acc: any[] = []) => {
  nodes.forEach((node) => {
    if (node.children?.length) flattenLeaves(node.children, acc);
    else acc.push(node);
  });
  return acc;
};

const caretSx = (open: boolean) => ({
  flex: "none",
  width: 0,
  height: 0,
  mr: 0.375,
  borderStyle: "solid",
  borderWidth: "0.3125rem 0 0.3125rem 0.375rem",
  borderColor: "transparent transparent transparent currentColor",
  transformOrigin: "30% 50%",
  transform: open ? "rotate(90deg)" : "none",
  transition: "transform .15s",
});

const rowSx = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  border: 0,
  background: "none",
  cursor: "pointer",
  textAlign: "left" as const,
  px: 0,
  py: 0.25,
  minHeight: "1.5rem",
};

export default function KbNav({
  kbTree,
  selectedSlug,
  setSelectedSlug,
}: {
  kbTree: any;
  selectedSlug: string;
  setSelectedSlug: any;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredNodes, setFilteredNodes] = useState(kbTree.categories_tree);
  const [loadingTree, setLoadingTree] = useState(false);
  const [expanded, setExpanded] = useState<string[]>([]);

  useEffect(() => {
    const slug = window.location.hash.substring(1);
    if (slug) setSelectedSlug(slug);
  }, [pathname]);

  const activeTrail = useMemo(
    () =>
      selectedSlug ? (pathToSlug(kbTree.categories_tree, selectedSlug) ?? []) : [],
    [selectedSlug, kbTree.categories_tree],
  );

  const isSearching = !!searchQuery.trim();

  const openSlugs = useMemo(() => {
    if (isSearching) return new Set(collectSlugs(filteredNodes));
    return new Set([...expanded, ...activeTrail]);
  }, [isSearching, filteredNodes, expanded, activeTrail]);

  const handleSelect = (slug: string) => {
    if (slug === selectedSlug) return;
    router.push(`${pathname}#${slug}`);
    setSelectedSlug(slug);
  };

  const toggle = (slug: string) =>
    setExpanded((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );

  const searchKnowledgebase = async (query: string): Promise<any[]> => {
    setLoadingTree(true);
    try {
      const response = await fetch(
        `/api/kb/search?query=${encodeURIComponent(query)}`,
      );
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message);
      }
      setLoadingTree(false);
      return data.data;
    } catch (error) {
      console.error("Search failed:", error);
      setLoadingTree(false);
      return [];
    }
  };

  const filterTree = async (nodes: any[], query: string): Promise<any[]> => {
    if (!query.trim()) {
      return nodes;
    }

    const searchResults = await searchKnowledgebase(query);
    const matchedSlugs = new Set(searchResults.map((result) => result.slug));

    const filterNode = (node: any): any => {
      if (matchedSlugs.has(node.slug)) {
        return node;
      }
      if (node.children?.length) {
        const filteredChildren = node.children.map(filterNode).filter(Boolean);

        if (filteredChildren.length > 0) {
          return { ...node, children: filteredChildren };
        }
      }

      return null;
    };
    return nodes.map(filterNode).filter(Boolean);
  };

  useEffect(() => {
    const updateFilteredNodes = async () => {
      const filtered = await filterTree(kbTree.categories_tree, searchQuery);
      setFilteredNodes(filtered);
    };

    updateFilteredNodes();
  }, [searchQuery, kbTree.categories_tree]);

  const renderNodes = (nodes: any[], depth = 0) => (
    <Box
      component="ul"
      sx={{
        listStyle: "none",
        m: 0,
        p: 0,
        ...(depth > 0
          ? {
              ml: 0.375,
              pl: 0.5,
              borderLeft: "1px solid",
              borderColor: "custom.ruleHairline",
            }
          : null),
      }}
    >
      {nodes.map((node: any) => {
        const hasChildren = node.children?.length > 0;
        const open = openSlugs.has(node.slug);
        const isActive = node.slug === selectedSlug;

        return (
          <Box component="li" key={node.slug} sx={{ m: 0 }}>
            {hasChildren ? (
              <Box
                component="button"
                type="button"
                onClick={() => toggle(node.slug)}
                aria-expanded={open}
                sx={{
                  ...rowSx,
                  fontFamily: FONT_UI,
                  fontSize: depth === 0 ? "0.8125rem" : "0.75rem",
                  fontWeight: 700,
                  letterSpacing: depth === 0 ? "0.06em" : 0,
                  textTransform: depth === 0 ? "uppercase" : "none",
                  color: "custom.ink",
                  "&:hover": { color: "primary.main" },
                }}
              >
                <Box component="span" aria-hidden="true" sx={caretSx(open)} />
                {node.name}
              </Box>
            ) : (
              <Box
                component="a"
                href={`#${node.slug}`}
                aria-current={isActive ? "page" : undefined}
                onClick={(event: React.MouseEvent) => {
                  event.preventDefault();
                  handleSelect(node.slug);
                }}
                sx={{
                  ...rowSx,
                  pl: 0.6875,
                  fontFamily: FONT_TEXT,
                  fontSize: "0.8125rem",
                  lineHeight: 1.4,
                  textDecoration: "none",
                  color: isActive ? "primary.main" : "custom.inkMuted",
                  fontWeight: isActive ? 700 : 400,
                  "&:hover": {
                    color: "primary.main",
                    textDecoration: "underline",
                  },
                }}
              >
                {node.name}
              </Box>
            )}
            {hasChildren && open ? renderNodes(node.children, depth + 1) : null}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box>
      <Kicker component="p" sx={{ mb: 0.375 }}>
        Contents
      </Kicker>
      <TextField
        fullWidth
        size="small"
        placeholder="Filter the knowledge base"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        slotProps={{ htmlInput: { "aria-label": "Filter the knowledge base" } }}
        sx={{ ...fieldSx, mb: 0.75 }}
      />
      {loadingTree ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : filteredNodes.length === 0 ? (
        <Box
          sx={{
            py: 1,
            fontFamily: FONT_UI,
            fontSize: "0.8125rem",
            color: "custom.inkMuted",
            ...hairline,
          }}
        >
          Nothing matches that filter.
        </Box>
      ) : (
        renderNodes(isSearching ? flattenLeaves(filteredNodes) : filteredNodes)
      )}
    </Box>
  );
}
